<script setup lang="ts">
import type { WithdrawStep } from '~/composables/useWithdrawModal';

const { current } = defineProps<{ current: WithdrawStep }>();

const { currentWithdrawMethod } = storeToRefs(useWalletStore());

// Mesma regra do stepper do depósito (CasinoGameDepositModalProgressBar), com
// 2 nós em vez de 3:
// - azul quando está na etapa (ativo) ou já passou por ela (concluído ✓)
// - cinza quando ainda não chegou
//
// O rótulo da Etapa 1 acompanha a família do método, como o título do topbar já
// fazia: "Chave pix" só descreve o Brasil — nos demais mercados o dado pedido é
// conta bancária ou telefone.
const steps = computed(() => [
  {
    id: 1,
    label: isPixWithdrawMethod(currentWithdrawMethod.value)
      ? 'account.wallet.withdraw_modal.step_pix_key_label'
      : 'account.wallet.withdraw_modal.step_account_title',
  },
  { id: 2, label: 'account.wallet.withdraw_modal.step_amount_label' },
]);

const isReached = (id: number) => current >= id;
const isDone = (id: number) => current > id;
</script>

<template>
  <div class="wdm-stepper">
    <div class="wdm-track">
      <template
        v-for="(s, index) in steps"
        :key="s.id"
      >
        <span
          v-if="index > 0"
          class="wdm-bar"
          :class="{ 'is-blue': isReached(s.id) }"
        />

        <div class="wdm-node">
          <span
            class="wdm-dot"
            :class="{ 'is-blue': isReached(s.id) }"
          >
            <NuxtIcon
              v-if="isDone(s.id)"
              name="check"
            />
            <template v-else>{{ s.id }}</template>
          </span>
          <span
            class="wdm-label"
            :class="{ 'is-blue': isReached(s.id) }"
          >
            {{ $t(s.label) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
$accent: #0485f7;
$muted: rgba(255, 255, 255, 0.2);

.wdm-stepper {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.wdm-track {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.wdm-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.wdm-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid $muted;
  color: $muted;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;

  // NuxtIcon ignora font-size/text-[Npx]: o tamanho vem do próprio arquivo, por
  // isso o svg é dimensionado no :deep.
  :deep(svg) {
    width: 9px;
    height: 9px;
  }

  // Atingido (ativo ou concluído): azul preenchido, texto/ícone branco.
  &.is-blue {
    background: $accent;
    border-color: transparent;
    color: #fff;
  }
}

.wdm-bar {
  flex: 1;
  max-width: 100px;
  height: 1px;
  margin-top: 8px;
  background: $muted;

  &.is-blue {
    background: $accent;
  }
}

.wdm-label {
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  color: $muted;

  &.is-blue {
    color: $accent;
  }
}
</style>
